import React, { useState, useEffect } from 'react';
import PhaseMatchCard from './PhaseMatchCard';
import MatchCard from './MatchCard';
import './Season2026.css';

function StatChip({ label, value, sub }) {
  return (
    <div className="season-stat-chip">
      <span className="season-stat-value">{value ?? '—'}</span>
      {sub && <span className="season-stat-sub">{sub}</span>}
      <span className="season-stat-label">{label}</span>
    </div>
  );
}

// Rounds with RID < this use logistic model predictions (genuinely pre-round).
// Round 6 (RID 3025) onwards uses Phase NN predictions.
const PHASE_NN_FROM_RID = 3025;

function PhaseSeason2026() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedRound, setSelectedRound] = useState(0);

  useEffect(() => {
    Promise.all([
      fetch('/phase_season_2026.json').then(r => {
        if (!r.ok) throw new Error('Season data not available');
        return r.json();
      }),
      fetch('/season_2026.json').then(r => r.ok ? r.json() : null).catch(() => null),
    ]).then(([phaseData, seasonData]) => {

      // Build mid → result lookup from season_2026.json
      const resultsByMid = {};
      // Build rid → logistic round lookup from season_2026.json
      const logisticRoundByRid = {};
      if (seasonData?.rounds) {
        for (const round of seasonData.rounds) {
          logisticRoundByRid[round.rid] = round;
          for (const match of round.matches || []) {
            if (match.mid != null && match.result != null) {
              resultsByMid[match.mid] = match.result;
            }
          }
        }
      }

      // Build unified rounds array
      const unifiedRounds = [];

      if (phaseData?.rounds) {
        for (const phaseRound of phaseData.rounds) {
          const useLogistic = phaseRound.rid < PHASE_NN_FROM_RID;
          const logisticRound = logisticRoundByRid[phaseRound.rid];

          if (useLogistic && logisticRound) {
            // Use logistic predictions with actual results, mark as logistic
            const matches = (logisticRound.matches || []).map(m => ({
              ...m,
              _model: 'logistic',
              result: m.result
                ? { ...m.result, correct: m.prediction.predicted_winner === m.result.winning_team }
                : null,
            }));
            const withResults = matches.filter(m => m.result != null);
            const correct = withResults.filter(m => m.result.correct).length;
            unifiedRounds.push({
              rid: phaseRound.rid,
              round_name: phaseRound.round_name,
              year: phaseRound.year,
              _model: 'logistic',
              matches,
              summary: {
                total: matches.length,
                home_wins: matches.filter(m => m.prediction.predicted_winner === m.home_team).length,
                away_wins: matches.filter(m => m.prediction.predicted_winner === m.away_team).length,
                avg_confidence: Math.round(
                  matches.reduce((s, m) => s + Math.max(m.prediction.home_win_prob, m.prediction.away_win_prob), 0)
                  / Math.max(matches.length, 1) * 100
                ),
                correct: withResults.length > 0 ? correct : null,
                accuracy: withResults.length > 0 ? Math.round(correct / withResults.length * 1000) / 10 : null,
              },
            });
          } else {
            // Use Phase NN predictions, merge results from season_2026.json
            const matches = (phaseRound.matches || []).map(m => {
              const rawResult = resultsByMid[m.mid] || null;
              return {
                ...m,
                _model: 'phase',
                result: rawResult
                  ? { ...rawResult, correct: m.prediction.predicted_winner === rawResult.winning_team }
                  : null,
              };
            });
            const withResults = matches.filter(m => m.result != null);
            const correct = withResults.filter(m => m.result.correct).length;
            unifiedRounds.push({
              ...phaseRound,
              _model: 'phase',
              matches,
              summary: {
                ...phaseRound.summary,
                correct: withResults.length > 0 ? correct : null,
                accuracy: withResults.length > 0 ? Math.round(correct / withResults.length * 1000) / 10 : null,
              },
            });
          }
        }
      }

      setData({ ...phaseData, rounds: unifiedRounds });
      // Default to the first round with no results (next unplayed round),
      // falling back to the last round if all are complete.
      const nextIdx = unifiedRounds.findIndex(r => !r.matches.some(m => m.result != null));
      setSelectedRound(nextIdx >= 0 ? nextIdx : unifiedRounds.length - 1);
      setLoading(false);
    }).catch(e => { setError(e.message); setLoading(false); });
  }, []);

  if (loading) return <div className="loading" style={{ minHeight: 400 }}>Loading phase season data...</div>;

  if (error || !data) return (
    <div className="next-round-container">
      <h2>Phase Model — 2026 Season</h2>
      <p style={{ color: '#7a9bb5' }}>
        Season data is not available. Run <code>make predict</code>.
      </p>
    </div>
  );

  const rounds = data.rounds || [];
  const currentRound = rounds[selectedRound] || null;
  const totalRounds = 25;

  // Compute season summary from merged data
  const allMatches = rounds.flatMap(r => r.matches);
  const matchesWithResults = allMatches.filter(m => m.result != null);
  const correct = matchesWithResults.filter(m => m.result.correct).length;
  const accuracy = matchesWithResults.length > 0
    ? Math.round(correct / matchesWithResults.length * 1000) / 10
    : null;
  const hcMatches = matchesWithResults.filter(m => m.prediction.confidence === 'high');
  const hcCorrect = hcMatches.filter(m => m.result.correct).length;
  const hcAccuracy = hcMatches.length > 0
    ? Math.round(hcCorrect / hcMatches.length * 1000) / 10
    : null;
  const roundsWithResults = rounds.filter(r => r.matches.some(m => m.result != null)).length;

  return (
    <div className="season-container">
      <div className="season-header">
        <h2>Season 2026</h2>
        <p className="season-subtitle">Predictions locked before each round · click any match to expand</p>
      </div>

      {/* Round selector — first thing after header so mobile lands here immediately */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, padding: '0 16px 12px', justifyContent: 'flex-start' }}>
        {rounds.map((r, idx) => {
          const hasResults = r.matches?.some(m => m.result != null);
          const isActive = idx === selectedRound;
          return (
            <button
              key={r.rid}
              onClick={() => setSelectedRound(idx)}
              style={{
                padding: '4px 10px',
                fontSize: '0.78rem',
                border: `1px solid ${hasResults ? '#2e7d32' : '#d1d5db'}`,
                borderRadius: 6,
                background: isActive ? '#1f2937' : '#ffffff',
                color: isActive ? '#ffffff' : '#374151',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              {r.round_name}
            </button>
          );
        })}
      </div>

      <div className="season-stats-banner">
        <StatChip
          label="Rounds completed"
          value={`${roundsWithResults} of ${totalRounds}`}
        />
        <StatChip
          label="Rounds predicted"
          value={`${rounds.length} of ${totalRounds}`}
        />
        <StatChip
          label="Overall accuracy"
          value={accuracy != null ? `${accuracy}%` : '—'}
          sub={matchesWithResults.length > 0 ? `${correct}/${matchesWithResults.length} games` : null}
        />
        <StatChip
          label="High confidence accuracy"
          value={hcAccuracy != null ? `${hcAccuracy}%` : '—'}
          sub={hcMatches.length > 0 ? `${hcCorrect}/${hcMatches.length} games` : null}
        />
      </div>

      <div className="round-content">
        {currentRound && (
          <>
            <div className="round-summary" style={{ marginBottom: 12 }}>
              <span>{currentRound.summary?.total} matches</span>
              <span>{currentRound.summary?.home_wins}H / {currentRound.summary?.away_wins}A</span>
              <span>avg confidence {currentRound.summary?.avg_confidence}%</span>
              {currentRound.summary?.accuracy != null && (
                <span style={{ color: '#69c08b', fontWeight: 600 }}>
                  {currentRound.summary.correct}/{currentRound.matches.filter(m => m.result).length} correct ({currentRound.summary.accuracy}%)
                </span>
              )}
              <span style={{ fontSize: '0.72rem', color: '#9ca3af', marginLeft: 4 }}>
                {currentRound._model === 'logistic' ? 'Logistic model' : 'Phase model'}
              </span>
            </div>
            {currentRound._model === 'logistic' ? (
              <div className="matches-grid">
                {currentRound.matches.map(match => (
                  <MatchCard key={match.mid} match={match} />
                ))}
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                {currentRound.matches.map(match => (
                  <PhaseMatchCard key={match.mid} match={match} />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default PhaseSeason2026;
